import { useEffect, useMemo, useRef, useState } from "react";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Bot, CheckCheck, Inbox, Loader2, MessageCircle, Paperclip,
  Phone, Plus, Search, Send, Settings2, Smile, Sparkles, StickyNote, Trash2, Zap,
} from "lucide-react";
import { useSalesAuth } from "@/lib/auth";

const QUICK = [
  "Hi, thanks for your interest in MeraStartup.",
  "Can we schedule a quick call today?",
  "Sharing our website plan options shortly.",
  "Please share your city and timeline.",
];

function initials(name, phone) {
  const n = (name || "").trim();
  if (n) return n.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "WA";
  return (phone || "WA").slice(-2);
}

function ago(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    const m = Math.round((Date.now() - d.getTime()) / 60000);
    if (m < 1) return "now";
    if (m < 60) return `${m} min`;
    if (m < 1440) return `${Math.round(m / 60)} hrs`;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
}

function isOpen(t) {
  return !t?.owner_id && (!t?.tag || t.tag === "unassigned");
}

export default function SalesWhatsApp() {
  const { user } = useSalesAuth();
  const [pageTab, setPageTab] = useState("inbox");
  const [st, setSt] = useState(null);
  const [ai, setAi] = useState({ enabled: false, auto_lead: false, auto_inbound: false, auto_unassigned: false, auto_claim: false, welcome_template_id: "", system_prompt: "" });
  const [threads, setThreads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState({ name: "", body: "", footer: "", buttons: [{ type: "reply", title: "" }] });
  const [myTag, setMyTag] = useState("");
  const [filter, setFilter] = useState("inbox");
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [teammates, setTeammates] = useState([]);
  const [active, setActive] = useState(null);
  const [lead, setLead] = useState(null);
  const [chat, setChat] = useState([]);
  const [draft, setDraft] = useState("");
  const [notes, setNotes] = useState("");
  const [composerTab, setComposerTab] = useState("reply");
  const [saving, setSaving] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ phone: "", name: "", body: "" });
  const [q, setQ] = useState("");
  const feedRef = useRef(null);

  const loadStatus = () => api.get("/sales/whatsapp/status").then(({ data }) => {
    setSt(data);
    setAi(data.ai || ai);
    setMyTag(data.me?.tag || "");
    setTeammates(data.sales_users || []);
    setUnassignedCount(data.unassigned_count || 0);
  }).catch((e) => toast.error(extractErr(e)));

  const loadTemplates = () => api.get("/sales/whatsapp/templates").then(({ data }) => {
    setTemplates(data.items || []);
  }).catch(() => {});

  const loadThreads = (f = filter) => api.get("/sales/whatsapp/threads", { params: { tag: f } }).then(({ data }) => {
    setThreads(data.items || []);
    setMyTag(data.my_tag || myTag);
    setUnassignedCount(data.unassigned_count || 0);
  }).catch((e) => toast.error(extractErr(e)));

  useEffect(() => { loadStatus(); loadTemplates(); }, []);
  useEffect(() => {
    loadThreads(filter);
    const id = setInterval(() => loadThreads(filter), 2000);
    return () => clearInterval(id);
  }, [filter]);

  useEffect(() => {
    const onWa = () => loadThreads(filter);
    window.addEventListener("sales:wa-new", onWa);
    return () => window.removeEventListener("sales:wa-new", onWa);
  }, [filter]);

  useEffect(() => {
    if (!active?.id) return undefined;
    const tid = active.id;
    const tick = () => {
      api.get(`/sales/whatsapp/threads/${tid}`).then(({ data }) => {
        setActive((cur) => (cur?.id === tid ? data.thread : cur));
        setChat(data.messages || []);
        if (data.lead) setLead(data.lead);
      }).catch(() => {});
    };
    const id = setInterval(tick, 1500);
    return () => clearInterval(id);
  }, [active?.id]);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [chat, active?.id]);

  const visible = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return threads;
    return threads.filter((t) => `${t.name || ""} ${t.phone || ""} ${t.last_message || ""}`.toLowerCase().includes(n));
  }, [threads, q]);

  const openThread = async (tid) => {
    try {
      const { data } = await api.get(`/sales/whatsapp/threads/${tid}`);
      setActive(data.thread);
      setChat(data.messages || []);
      setLead(data.lead || null);
      setNotes(data.thread?.notes || "");
      setComposerTab("reply");
      if (data.sales_users) setTeammates(data.sales_users);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const saveAi = async () => {
    try {
      const { data } = await api.put("/sales/whatsapp/ai", ai);
      setAi(data);
      toast.success("Your AI training was saved — only your tagged clients use this prompt");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const saveTpl = async () => {
    if (!tpl.name.trim() || !tpl.body.trim()) {
      toast.error("Template needs a name and a message");
      return;
    }
    try {
      await api.post("/sales/whatsapp/templates", {
        ...tpl,
        buttons: (tpl.buttons || []).filter((b) => (b.title || "").trim()),
      });
      setTpl({ name: "", body: "", footer: "", buttons: [{ type: "reply", title: "" }] });
      loadTemplates();
      toast.success("Template saved — send it from any chat (24h window for custom buttons)");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const deleteTpl = async (id) => {
    try {
      await api.delete(`/sales/whatsapp/templates/${id}`);
      loadTemplates();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const sendTpl = async (id) => {
    if (!active) {
      toast.error("Open a chat first");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/sales/whatsapp/send", { thread_id: active.id, phone: active.phone, template_id: id });
      setActive(data.thread);
      setChat(data.messages || []);
      loadThreads();
      toast.success("Template sent");
    } catch (e) { toast.error(extractErr(e)); }
    finally { setSaving(false); }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim() || !active) return;
    setSaving(true);
    try {
      const { data } = await api.post("/sales/whatsapp/send", { thread_id: active.id, phone: active.phone, body: draft });
      setDraft("");
      setActive(data.thread);
      setChat(data.messages || []);
      loadThreads();
    } catch (err) { toast.error(extractErr(err)); }
    finally { setSaving(false); }
  };

  const sendNew = async (e) => {
    e.preventDefault();
    if (!compose.phone.trim() || !compose.body.trim()) {
      toast.error("Enter a phone number and a message");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/sales/whatsapp/send", {
        phone: compose.phone, name: compose.name, body: compose.body,
      });
      setCompose({ phone: "", name: "", body: "" });
      setComposeOpen(false);
      setActive(data.thread);
      setChat(data.messages || []);
      setPageTab("inbox");
      setFilter("mine");
      loadThreads("mine");
      toast.success("Message sent — tagged as yours");
    } catch (err) { toast.error(extractErr(err)); }
    finally { setSaving(false); }
  };

  const assignTo = async (salesId) => {
    if (!active) return;
    try {
      const { data } = await api.post(`/sales/whatsapp/threads/${active.id}/assign`, { assigned_to: salesId });
      loadThreads();
      loadStatus();
      if (data.thread?.owner_id && data.thread.owner_id !== user?.id) {
        setActive(null);
        setChat([]);
        setLead(null);
      } else {
        setActive(data.thread);
        setChat(data.messages || []);
      }
      toast.success(`Tagged ${data.thread?.tag || "assigned"}`);
    } catch (e) { toast.error(extractErr(e)); }
  };

  const saveNotes = async () => {
    if (!active) return;
    try {
      const { data } = await api.put(`/sales/whatsapp/threads/${active.id}/notes`, { notes });
      setActive(data.thread);
      toast.success("Internal note saved");
    } catch (e) { toast.error(extractErr(e)); }
  };

  const unassigned = active && isOpen(active);

  return (
    <div className="-m-4 sm:-m-6 lg:-m-8 h-[calc(100dvh-3.5rem)] md:h-[100dvh] flex flex-col bg-[#f4f6f8] text-slate-800">
      <div className="shrink-0 px-4 py-3 border-b bg-white flex flex-wrap items-center gap-2">
        <MessageCircle className="w-5 h-5 text-emerald-600" />
        <div className="font-semibold text-slate-900 mr-2">WhatsApp</div>
        <button type="button" onClick={() => setPageTab("inbox")} className={`px-3 py-1.5 rounded-full text-sm ${pageTab === "inbox" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="wa-tab-inbox">
          <Inbox className="w-3.5 h-3.5 inline mr-1" /> Inbox
        </button>
        <button type="button" onClick={() => setPageTab("settings")} className={`px-3 py-1.5 rounded-full text-sm ${pageTab === "settings" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="wa-tab-settings">
          <Settings2 className="w-3.5 h-3.5 inline mr-1" /> WhatsApp settings
        </button>
        <div className="ml-auto text-xs text-slate-500 hidden sm:block">Manish&apos;s client style tags · shared Cloud API number</div>
      </div>

      {pageTab === "settings" ? (
        <div className="flex-1 overflow-y-auto p-5 space-y-4 max-w-3xl">
          <div className="rounded-2xl border bg-white p-5 shadow-sm" data-testid="wa-shared-status">
            <div className="font-semibold text-slate-900">Team number {st?.linked ? <Badge className="ml-2 bg-emerald-50 text-emerald-700 border-emerald-200">Linked</Badge> : <Badge className="ml-2 bg-orange-50 text-orange-700 border-orange-200">Not linked</Badge>}</div>
            <p className="text-slate-500 text-sm mt-2">
              {st?.linked
                ? `Sending as ${st.display_phone ? `+${st.display_phone}` : "the shared business number"}. Super Admin manages the Cloud API under Facebook Setup.`
                : "Ask Super Admin to link WhatsApp Cloud API on Facebook Setup. Sales logins cannot change the number."}
            </p>
          </div>
          <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-3" data-testid="wa-ai-form">
            <div className="font-semibold">Train your AI {st?.openrouter_configured ? <span className="text-xs text-emerald-600 font-normal">OpenRouter key configured</span> : <span className="text-xs text-orange-600 font-normal">add OpenRouter key in Super Admin → Integrations</span>}</div>
            <p className="text-xs text-slate-500">This prompt is yours only. Turn on auto-reply to let OpenRouter answer incoming WhatsApp for you — including unassigned chats if you enable that. Custom-button templates can greet new chats automatically.</p>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ai.enabled} onChange={(e) => setAi({ ...ai, enabled: e.target.checked })} /> Enable AI</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ai.auto_lead} onChange={(e) => setAi({ ...ai, auto_lead: e.target.checked })} /> Auto-message new Facebook leads assigned to me</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ai.auto_inbound} onChange={(e) => setAi({ ...ai, auto_inbound: e.target.checked })} /> Auto-reply to incoming WhatsApp on my tag</label>
            <label className="flex items-center gap-2 text-sm" data-testid="wa-ai-auto-unassigned"><input type="checkbox" checked={ai.auto_unassigned} onChange={(e) => setAi({ ...ai, auto_unassigned: e.target.checked })} /> Also auto-reply to unassigned chats (full inbox automation)</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={ai.auto_claim} onChange={(e) => setAi({ ...ai, auto_claim: e.target.checked })} /> Claim unassigned chats as mine when AI replies</label>
            <div>
              <Label>Welcome template (optional first auto-reply with buttons)</Label>
              <select className="mt-1 w-full h-9 rounded-md border border-slate-200 px-2 text-sm" value={ai.welcome_template_id || ""} onChange={(e) => setAi({ ...ai, welcome_template_id: e.target.value })} data-testid="wa-ai-welcome-template">
                <option value="">AI-written text (no buttons)</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Train the agent (system prompt)</Label>
              <Textarea rows={7} className="mt-1" value={ai.system_prompt} onChange={(e) => setAi({ ...ai, system_prompt: e.target.value })} />
            </div>
            <Button type="button" className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={saveAi}>Save AI</Button>
          </div>
          <div className="rounded-2xl border bg-white p-5 shadow-sm space-y-3" data-testid="wa-templates-form">
            <div className="font-semibold">Message templates with custom buttons</div>
            <p className="text-xs text-slate-500">Reply buttons and a website button send inside the 24-hour customer window. Phone buttons are added as text. Submit to Meta later if you need to message numbers that have not written first.</p>
            <div>
              <Label>Template name</Label>
              <Input className="mt-1" value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} placeholder="Welcome offer" data-testid="wa-tpl-name" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={4} className="mt-1" value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} data-testid="wa-tpl-body" />
            </div>
            <div>
              <Label>Footer (optional)</Label>
              <Input className="mt-1" value={tpl.footer} onChange={(e) => setTpl({ ...tpl, footer: e.target.value })} placeholder="MeraStartup" />
            </div>
            <div className="space-y-2">
              <Label>Buttons (up to 3)</Label>
              {(tpl.buttons || []).map((b, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center">
                  <select className="h-9 rounded-md border px-2 text-sm" value={b.type} onChange={(e) => {
                    const buttons = [...tpl.buttons]; buttons[i] = { ...buttons[i], type: e.target.value }; setTpl({ ...tpl, buttons });
                  }}>
                    <option value="reply">Reply</option>
                    <option value="url">Website</option>
                    <option value="phone">Call</option>
                  </select>
                  <Input className="flex-1 min-w-[120px]" placeholder="Button title" value={b.title || ""} onChange={(e) => {
                    const buttons = [...tpl.buttons]; buttons[i] = { ...buttons[i], title: e.target.value }; setTpl({ ...tpl, buttons });
                  }} data-testid={`wa-tpl-btn-title-${i}`} />
                  {b.type === "url" && <Input className="flex-1 min-w-[160px]" placeholder="https://" value={b.url || ""} onChange={(e) => {
                    const buttons = [...tpl.buttons]; buttons[i] = { ...buttons[i], url: e.target.value }; setTpl({ ...tpl, buttons });
                  }} />}
                  {b.type === "phone" && <Input className="w-40" placeholder="+91…" value={b.phone || ""} onChange={(e) => {
                    const buttons = [...tpl.buttons]; buttons[i] = { ...buttons[i], phone: e.target.value }; setTpl({ ...tpl, buttons });
                  }} />}
                </div>
              ))}
              {(tpl.buttons || []).length < 3 && (
                <Button type="button" size="sm" variant="outline" onClick={() => setTpl({ ...tpl, buttons: [...(tpl.buttons || []), { type: "reply", title: "" }] })} data-testid="wa-tpl-add-button">Add button</Button>
              )}
            </div>
            <Button type="button" className="bg-emerald-600 text-white" onClick={saveTpl} data-testid="wa-tpl-save">Save template</Button>
            <div className="divide-y border rounded-lg">
              {templates.length === 0 && <p className="p-3 text-sm text-slate-400">No templates yet.</p>}
              {templates.map((t) => (
                <div key={t.id} className="p-3 flex gap-2 items-start" data-testid={`wa-tpl-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500 whitespace-pre-wrap">{t.body}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(t.buttons || []).map((b) => <span key={b.id || b.title} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-100">{b.title}</span>)}
                    </div>
                  </div>
                  <button type="button" className="p-1 text-slate-400 hover:text-red-600" onClick={() => deleteTpl(t.id)} aria-label="Delete template"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)_280px]">
          <aside className="bg-white border-r flex flex-col min-h-0 relative">
            <div className="p-3 space-y-2 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <Input className="pl-9 h-9 bg-slate-50" placeholder="Search chats" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-1">
                <button type="button" onClick={() => setFilter("inbox")} className={`px-2 py-1 rounded-md text-[11px] border ${filter === "inbox" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-500"}`} data-testid="wa-filter-inbox">Chat Status</button>
                <button type="button" onClick={() => setFilter("unassigned")} className={`px-2 py-1 rounded-md text-[11px] border ${filter === "unassigned" ? "bg-orange-50 border-orange-300 text-orange-800" : "border-slate-200 text-slate-500"}`} data-testid="wa-filter-unassigned">Unassigned{unassignedCount ? ` (${unassignedCount})` : ""}</button>
                <button type="button" onClick={() => setFilter("mine")} className={`px-2 py-1 rounded-md text-[11px] border ${filter === "mine" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "border-slate-200 text-slate-500"}`} data-testid="wa-filter-mine">Assignee · mine</button>
              </div>
              <div className="text-[11px] text-slate-400">Showing {visible.length} chats · {myTag || "your tag"}</div>
            </div>
            {unassignedCount > 0 && (
              <div className="mx-3 mt-2 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-800">
                Auto-assign unassigned chats: claim from the conversation header.
              </div>
            )}
            <div className="flex-1 overflow-y-auto">
              {visible.length === 0 && <p className="p-4 text-sm text-slate-400">No conversations in this view.</p>}
              {visible.map((t) => (
                <button key={t.id} type="button" onClick={() => openThread(t.id)} className={`w-full text-left px-3 py-3 flex gap-3 border-b border-slate-50 ${active?.id === t.id ? "bg-emerald-50" : "hover:bg-slate-50"}`} data-testid={`wa-thread-${t.id}`}>
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-xs font-bold shrink-0">{initials(t.name, t.phone)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between gap-2">
                      <div className="font-medium text-sm text-slate-900 truncate">{t.name || t.phone}</div>
                      <div className="text-[10px] text-slate-400 shrink-0">{ago(t.last_at)}</div>
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{t.last_message}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${isOpen(t) ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}`}>{isOpen(t) ? "Unassigned" : (t.tag || t.owner_name)}</span>
                      {t.unread && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <button type="button" onClick={() => { setComposeOpen(true); }} className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-emerald-600 text-white shadow-lg flex items-center justify-center hover:bg-emerald-500" data-testid="wa-new-message">
              <Plus className="w-6 h-6" />
            </button>
          </aside>

          <section className="flex flex-col min-h-0 bg-[#ece5dd]">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Pick a conversation or start a new message.</div>
            ) : (
              <>
                <div className="shrink-0 bg-white px-4 py-2.5 border-b flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{active.name || active.phone}</div>
                    <div className="text-[11px] text-slate-500">{active.phone}</div>
                  </div>
                  {active.tag && active.tag !== "unassigned"
                    ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{active.tag}</Badge>
                    : <Badge className="bg-orange-50 text-orange-700 border-orange-200" data-testid="wa-unassigned-badge">Unassigned</Badge>}
                  <Button type="button" size="sm" variant="outline" onClick={() => assignTo(user?.id)} data-testid="wa-claim">Tag as mine</Button>
                  <select className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs" value={active.owner_id || ""} onChange={(e) => { if (e.target.value) assignTo(e.target.value); }} data-testid="wa-assign-select">
                    <option value="">{unassigned ? "Assign chat" : "Reassign"}</option>
                    {teammates.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <a className="p-2 text-slate-500 hover:text-emerald-700" href={`tel:+${String(active.phone || "").replace(/\D/g, "")}`} title="Call"><Phone className="w-4 h-4" /></a>
                </div>
                <div ref={feedRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="wa-messages">
                  {chat.map((m) => (
                    <div key={m.id} className={`max-w-[78%] rounded-lg px-3 py-2 text-sm shadow-sm ${m.direction === "out" ? "ml-auto bg-[#dcf8c6] text-slate-900" : "bg-white text-slate-800"}`}>
                      {m.body}
                      {(m.buttons || []).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {m.buttons.map((b) => (
                            <div key={b.id || b.title} className="text-xs text-center rounded-md border border-emerald-200 bg-white/80 py-1 text-emerald-800">{b.title}</div>
                          ))}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1 justify-end">
                        {m.ai ? <><Bot className="w-3 h-3" /> Automated</> : (m.from_sales_name || (m.direction === "in" ? "customer" : ""))}
                        {m.direction === "out" && <CheckCheck className="w-3 h-3 text-sky-500" />}
                        {m.created_at && <span>{new Date(m.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="shrink-0 bg-white border-t">
                  <div className="flex text-sm border-b">
                    <button type="button" className={`px-4 py-2 ${composerTab === "reply" ? "text-emerald-700 border-b-2 border-emerald-600" : "text-slate-500"}`} onClick={() => setComposerTab("reply")}>Reply</button>
                    <button type="button" className={`px-4 py-2 ${composerTab === "notes" ? "text-emerald-700 border-b-2 border-emerald-600" : "text-slate-500"}`} onClick={() => setComposerTab("notes")}><StickyNote className="w-3.5 h-3.5 inline mr-1" />Notes</button>
                  </div>
                  {composerTab === "notes" ? (
                    <div className="p-3 space-y-2">
                      <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal team note — customer does not see this" />
                      <Button size="sm" className="bg-slate-800 text-white" onClick={saveNotes}>Save note</Button>
                    </div>
                  ) : (
                    <form onSubmit={send} className="p-3 space-y-2">
                      <div className="flex flex-wrap gap-1">
                        {QUICK.map((t) => (
                          <button key={t} type="button" className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-50" onClick={() => setDraft(t)}><Zap className="w-3 h-3 inline mr-0.5" />{t.slice(0, 28)}…</button>
                        ))}
                        {templates.map((t) => (
                          <button key={t.id} type="button" className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100" onClick={() => sendTpl(t.id)} data-testid={`wa-send-tpl-${t.id}`}>
                            <Sparkles className="w-3 h-3 inline mr-0.5" />{t.name}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="flex gap-1 text-slate-400 pb-2">
                          <button type="button" className="p-1 hover:text-emerald-700" title="Quick replies" onClick={() => setDraft(QUICK[0])}><Zap className="w-4 h-4" /></button>
                          <button type="button" className="p-1 hover:text-emerald-700" title="Templates" onClick={() => setDraft(QUICK[1])}><Sparkles className="w-4 h-4" /></button>
                          <button type="button" className="p-1 hover:text-emerald-700" title="Emoji" onClick={() => setDraft((d) => `${d} 🙂`)}><Smile className="w-4 h-4" /></button>
                          <button type="button" className="p-1 hover:text-slate-500" title="Attachments" onClick={() => toast.message("Media attachments need a Cloud API template — text is live now.")}><Paperclip className="w-4 h-4" /></button>
                        </div>
                        <Input className="flex-1" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message…" data-testid="wa-draft" />
                        <Button type="submit" disabled={saving || !st?.linked} className="bg-emerald-600 hover:bg-emerald-500 text-white"><Send className="w-4 h-4 mr-1" /> Send</Button>
                      </div>
                    </form>
                  )}
                </div>
              </>
            )}
          </section>

          <aside className="hidden lg:flex flex-col bg-white border-l min-h-0">
            <div className="px-4 py-3 border-b font-semibold text-slate-900">Contact details</div>
            {!active ? <p className="p-4 text-sm text-slate-400">Select a chat.</p> : (
              <div className="p-4 space-y-4 overflow-y-auto text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold">{initials(active.name, active.phone)}</div>
                  <div>
                    <div className="font-semibold text-slate-900">{active.name || "WhatsApp user"}</div>
                    <div className="text-xs text-slate-500">{lead?.source ? `via ${lead.source}` : "WhatsApp"}</div>
                  </div>
                </div>
                <div className="space-y-2 text-slate-600">
                  <div><span className="text-[11px] uppercase text-slate-400">Contact</span><div>{active.phone || "—"}</div></div>
                  <div><span className="text-[11px] uppercase text-slate-400">Email</span><div>{lead?.email || "—"}</div></div>
                  <div><span className="text-[11px] uppercase text-slate-400">Account owner</span><div>{active.owner_name || "Unassigned"}</div></div>
                  <div><span className="text-[11px] uppercase text-slate-400">Status</span><div className="capitalize">{lead?.status || (isOpen(active) ? "open" : "tagged")}</div></div>
                  <div><span className="text-[11px] uppercase text-slate-400">WhatsApp opted</span><div>Yes</div></div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-400 mb-1">Tags</div>
                  <div className="flex flex-wrap gap-1">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{isOpen(active) ? "Unassigned" : (active.tag || myTag)}</span>
                  </div>
                </div>
                {lead?.answers?.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase text-slate-400">CRM answers</div>
                    {lead.answers.slice(0, 6).map((a) => (
                      <div key={a.key}><div className="text-[11px] text-slate-400">{a.label}</div><div>{a.value}</div></div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-400">Notes live in the conversation Notes tab.</p>
              </div>
            )}
          </aside>
        </div>
      )}

      {composeOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setComposeOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-xl space-y-3" onClick={(e) => e.stopPropagation()} data-testid="wa-compose-form">
            <div className="font-semibold">New message</div>
            <p className="text-xs text-slate-500">Use country code (e.g. 9198…). Sending tags the chat as {user?.name || "you"}&apos;s client. Meta only delivers free-form text if they messaged you in the last 24 hours.</p>
            <div>
              <Label>WhatsApp number</Label>
              <Input className="mt-1" value={compose.phone} onChange={(e) => setCompose({ ...compose, phone: e.target.value })} placeholder="9198…" data-testid="wa-compose-phone" />
            </div>
            <div>
              <Label>Name (optional)</Label>
              <Input className="mt-1" value={compose.name} onChange={(e) => setCompose({ ...compose, name: e.target.value })} placeholder="Client name" data-testid="wa-compose-name" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={3} className="mt-1" value={compose.body} onChange={(e) => setCompose({ ...compose, body: e.target.value })} data-testid="wa-compose-body" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button type="button" disabled={saving || !st?.linked} onClick={sendNew} className="bg-emerald-600 text-white" data-testid="wa-compose-send">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
